/**
 * Static per-page narration fallback used by Guide Mode.
 *
 * Pipeline:
 *   1) Frontend calls GET /api/voice-tours/active on app boot.
 *   2) Each override doc { path, label, body, auto_scroll, extra_exit_pause_ms,
 *      enabled } is merged on top of the static defaults below.
 *   3) When the tour walks a path, `findPageNarration(path)` returns the
 *      merged result. `enabled: false` means the page is skipped entirely.
 *   4) Admin saves an override at /admin/voice-tours → emits a
 *      `voice-tour-overrides-changed` window event → live refresh.
 *
 * Keep bodies short (≤220 chars) — they're spoken aloud and read on a card.
 */
export const STATIC_PAGE_NARRATIONS = {
  '/artist': {
    label: 'Dashboard',
    body: 'This is your dashboard, your home base. Submission Progress shows your funnel. Recent Submissions is your real-time feed. And Quick Actions are shortcuts to upload or submit a song.',
    overview_tour: 'page_overview_dashboard',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/artist/songs': {
    label: 'My Songs',
    body: 'Your music library. Upload tracks here, edit titles or genres, and submit straight to executives, radio, or curators.',
    overview_tour: 'page_overview_songs',
    chapters: [
      {
        highlightSelector: '[data-testid="create-song-btn"]',
        // closeAfter: true → press Escape AFTER the voice fully ends.
        // This guarantees the last sentence ("...the Create Song button
        // saves it to your library.") is never clipped by the dialog
        // close animation. NEVER schedule a `close` demoStep on the
        // tail end of a chapter — it will cut the voice off.
        closeAfter: true,
        // Opt out of auto-scroll for this chapter — we open a modal and
        // the demoSteps explicitly scroll specific form elements into
        // view. A parallel page-level auto-scroll would fight with that.
        autoScroll: false,
        // Live demo of the Add New Song flow. ALL `at` offsets are anchored
        // to the moment the audio actually starts playing (via the
        // playTTS onPlayStart hook), so phrase alignment is independent
        // of ElevenLabs fetch latency.
        // Voice utterance order with rough mid-phrase landings:
        //   t≈0.0s  "My Songs."
        //   t≈1.2s  "Add New Song."
        //   t≈2.6s  "Watch — I will open the Add New Song form for you."
        //   t≈6.5s  "Here we type the song title, like Midnight Drive,"
        //   t≈10.0s "then drop in your MP3 or wave file."
        //   t≈13.0s "Pick up to three genres,"
        //   t≈14.5s "add your cover art so your track looks the part,"
        //   t≈18.5s "and once everything looks right, the Create Song
        //            button saves it to your library."
        demoSteps: [
          // Open the dialog the moment the voice says
          // "I will open the Add New Song form" — never before.
          { at: 3200,  type: 'click', selector: '[data-testid="create-song-btn"]' },
          // Type "Midnight Drive" while voice says "Here we type the song title…"
          { at: 6800,  type: 'type',  selector: '[data-testid="song-title-input"]', text: 'Midnight Drive', perCharDelayMs: 90 },
          // Pulse the audio dropzone while voice says "drop in your MP3 or wave file"
          { at: 10000, type: 'pulse', selector: '[data-testid="audio-upload-dropzone"]', durationMs: 2800 },
          // Genre clicks while voice says "Pick up to three genres"
          { at: 13200, type: 'click', selector: '[data-testid="genre-option-pop"]' },
          { at: 14400, type: 'click', selector: '[data-testid="genre-option-hip-hop"]' },
          // Pulse Create Song while voice says "the Create Song button"
          { at: 19500, type: 'pulse', selector: '[data-testid="create-song-submit"]', durationMs: 2200 },
          // No `close` here — closeAfter handles it AFTER the voice ends.
        ],
        label: 'Add New Song',
        body: 'Watch — I will open the Add New Song form for you. Here we type the song title, like "Midnight Drive", then drop in your MP3 or wave file. Pick up to three genres, add your cover art so your track looks the part, and once everything looks right, the Create Song button saves it to your library.',
      },
      {
        highlightSelector: '[data-testid="submit-song-btn"]',
        ctaSelector: '[data-testid="submit-song-btn"]',
        label: 'Submit Song',
        body: 'Once a track is in your library, the Submit Song button sends it straight to executives, radio stations, or playlist curators on the Discover page.',
      },
      {
        highlightSelector: '[data-testid="songs-list"], [data-testid="add-first-song-btn"]',
        autoScroll: true,
        label: 'Your library',
        body: 'Every track you have uploaded is here. Edit titles or genres anytime, replace cover art, or delete songs you no longer want listed.',
      },
    ],
  },
  '/artist/discover': {
    label: 'Discover & Submit',
    body: 'Browse executives, radio stations, and playlist curators. Filter by genre, then submit straight from any card to send your music.',
    overview_tour: 'page_overview_discover',
    // Extra breath before auto-advancing to My Submissions — the last
    // chapter's Preview Playlist demo was getting clipped at the GRACE
    // boundary on slower networks.
    extraExitPauseMs: 800,
    // Chaptered narration: while speaking each section, also switch the
    // active tab so the user SEES the listings the voice is describing.
    chapters: [
      {
        tabSelector: '[data-testid="discovery-type-executives"]',
        autoScroll: true,
        label: 'Executives',
        body: 'Keep watching — I am driving the tour for you. A and R professionals who can listen privately and respond with feedback, meetings, or invites. This is where most artists start.',
      },
      {
        tabSelector: '[data-testid="discovery-type-radio"]',
        autoScroll: true,
        label: 'Radio Stations',
        body: 'Get your record in rotation on college stations across the United States. Target specific markets by advertising on their stations when you have a new release or a pre-release coming up.',
      },
      {
        tabSelector: '[data-testid="discovery-type-playlists"]',
        autoScroll: true,
        label: 'Playlist Curators',
        body: 'Get placed on curated playlists in your genre — a great way to grow streaming numbers organically.',
      },
      {
        // Stay on playlists tab — open the playlist preview dialog DURING
        // the voice so the user sees the demo in real time, then close it
        // cleanly with Escape so we don't leave dead air on screen.
        highlightSelector: '[data-testid="preview-playlist-btn"]',
        clickDuringVoice: '[data-testid="preview-playlist-btn"]',
        closeAfter: '[data-testid="spotify-embed-close-btn"]',
        // Opt out of auto-scroll — the Spotify preview dialog covers the
        // page; scrolling the body underneath it serves no purpose.
        autoScroll: false,
        label: 'Preview a Playlist',
        body: 'On any card you can preview the playlist before submitting. If it feels like a fit, submit straight from that card.',
      },
    ],
  },
  '/artist/submissions': {
    label: 'My Submissions',
    body: 'Every track you have sent out, with current status — pending, favorited, passed, or VIP invite.',
    overview_tour: 'page_overview_submissions',
    // Extra breath before auto-advancing to Song Tickets — the Decided
    // chapter's wrap-up was being clipped at the GRACE boundary.
    extraExitPauseMs: 800,
    chapters: [
      {
        tabSelector: '[data-testid="submissions-tab-executives"]',
        label: 'Executives',
        body: 'This filter shows every track you have sent to A and R executives. These are the deepest decisions — feedback, meetings, or VIP invites land here.',
      },
      {
        tabSelector: '[data-testid="submissions-tab-radio"]',
        label: 'Radio',
        body: 'All your college radio submissions. Get your record in rotation across the United States and watch this tab for spins, ad campaigns, and station feedback.',
      },
      {
        tabSelector: '[data-testid="submissions-tab-curators"]',
        label: 'Curators',
        body: 'Every playlist curator submission you have made. When a curator decides, you can rate them right from the card to keep the community honest.',
      },
      {
        tabSelector: '[data-testid="submissions-tab-pending"]',
        label: 'Pending',
        body: 'Anything still waiting for a decision is here, so you always know what is in flight.',
      },
      {
        tabSelector: '[data-testid="submissions-tab-decided"]',
        label: 'Decided',
        body: 'Final outcomes — favorited, passed, or invited. Open any card to read the full decision and any feedback.',
      },
    ],
  },
  '/artist/wallet': {
    label: 'Wallet',
    body: 'Your credit balance and transaction log. Top up via Stripe inline. Members get up to fifty percent off every pack.',
    overview_tour: 'page_overview_wallet',
    chapters: [
      {
        highlightSelector: '[data-testid="wallet-balance-card"]',
        label: 'Available Balance',
        body: 'This card shows how many credits you have right now. Credits are how you submit songs to executives, radio, and curators on the platform.',
      },
      {
        highlightSelector: '[data-testid="wallet-credit-packs"]',
        ctaSelector: '[data-popular-cta="true"]',
        label: 'Buy Credits',
        body: 'Pick a pack that fits how often you submit. Pay As You Go is fifty dollars for twenty five credits, with bigger packs available. Monthly members save forty percent on every pack. Yearly members save fifty percent.',
      },
      {
        highlightSelector: '[data-testid="wallet-activity"]',
        label: 'Activity',
        body: 'Every credit you spend or top up shows up here, so you always know where your balance went.',
      },
    ],
  },
  '/artist/membership': {
    label: 'Membership',
    body: 'There are memberships for every level of artist, plus pay as you go if you want zero monthly cost. Take a look at the cards and pick the one that matches your hustle.',
    overview_tour: 'page_overview_membership',
  },
  '/artist/referrals': {
    label: 'Refer & Earn',
    body: 'Share your link — when a friend signs up and finishes onboarding, both of you get 10 credits.',
    overview_tour: 'page_overview_referrals',
  },
  '/artist/messages': {
    label: 'Messages',
    body: 'Direct line with executives, curators, and stations who decided on your submissions.',
    overview_tour: 'page_overview_messages',
    extraExitPauseMs: 800,
  },
  '/artist/tickets': {
    label: 'Song Tickets',
    body: 'Activates when an executive wants to coach you on a track. The ticket is where you engage — these are major opportunities.',
  },
  '/artist/rate-curators': {
    label: 'Rate Curators',
    body: 'Rate curators who reviewed your tracks. Your ratings keep the curator community honest.',
  },
  '/artist/imports': {
    label: 'Import Data',
    body: 'Pro feature for bulk-uploading entire albums in one shot.',
  },
  '/artist/support': {
    label: 'Support',
    body: 'Open a ticket, browse FAQs, or chat with us 24/7 — we respond fast.',
    extraExitPauseMs: 1000,
    chapters: [
      {
        label: 'Support',
        body: 'This is where you reach the team. Open a ticket, browse FAQs, or chat with us twenty-four seven — we respond fast.',
      },
    ],
  },

  // ---- Executive Voice Tour ------------------------------------------------
  // Same self-driving, locked, chaptered behavior as the Artist tour; just
  // different messaging framed around reviewing, scouting, coaching, and
  // earnings. Tour ends on /executive/support with the finale CTA.
  '/executive': {
    label: 'Dashboard',
    body: 'This is your executive dashboard, your home base. Pending Submissions, decided reviews, your Hit List, and your active scouts — all at a glance.',
    extraExitPauseMs: 800,
  },
  '/executive/queue': {
    label: 'Submission Queue',
    body: 'This is your review queue. Pending artist submissions land here — listen, rate, and decide whether to pass, save to the Hit List, or open a Song Ticket to coach the artist. Every review you make compounds your influence on the platform.',
    extraExitPauseMs: 800,
  },
  '/executive/charts': {
    label: 'Hit List Charts',
    body: 'A Billboard-style leaderboard of the top-rated songs on the platform. Weekly movement, trending tracks, and the artists worth watching — you see what the entire executive network is gravitating toward in real time.',
    extraExitPauseMs: 800,
  },
  '/executive/tickets': {
    label: 'Song Tickets',
    body: 'When a track catches your ear, you can suggest working on it together and open a Song Ticket. From there, you and the artist go back and forth over email — your written notes, their revisions, your reactions to the next pass. It is the cleanest way to take a record from promising to undeniable, with both of you focused on the same thing — making a great song.',
    extraExitPauseMs: 800,
  },
  '/executive/saved-songs': {
    label: 'Saved Songs',
    body: 'Tracks you saved from the Hit List for a second listen. Come back whenever you want to re-rate, reach out to the artist, or open a Song Ticket to coach them.',
    extraExitPauseMs: 800,
  },
  '/executive/scouts': {
    label: 'My Scouts',
    body: 'Build and manage your own talent scouting team. Invite scouts, assign them genres or regions, and let them surface submissions for you to review. Scouts amplify your reach without diluting your taste.',
    extraExitPauseMs: 800,
  },
  '/executive/scout-performance': {
    label: 'Scout Performance',
    body: 'Track how your scouts are performing — submissions sent, agreement rate with your decisions, and the tracks they surfaced that made your Hit List. This is how you identify your best scouts and reward them.',
    extraExitPauseMs: 800,
  },
  '/executive/payouts': {
    label: 'Payouts and Earnings',
    body: 'Your revenue home base. Add your payment information here — PayPal, Zelle, Venmo, Cash App, or bank transfer. We pay out monthly, on time, every month. Every dollar from inbox submissions, evaluations, video calls, and song tickets rolls into one payout.',
    extraExitPauseMs: 1000,
  },
  '/executive/services': {
    label: 'My Services',
    body: 'This is where you control how you work. Toggle Direct to Inbox, Song Evaluations, Video Calls, and Song Tickets on or off. Set your prices in credits. Pick the four evaluation questions you want to answer. Change anything anytime.',
    extraExitPauseMs: 800,
  },
  '/executive/membership': {
    label: 'Membership',
    body: 'Your plan and benefits. Free gets you started with one scout. Paid tiers unlock more scouts, faster decisions, priority on the Hit List, and higher payout thresholds.',
    extraExitPauseMs: 800,
  },
  '/executive/messages': {
    label: 'Messages',
    body: 'Direct messaging with artists, scouts, and other executives on the platform. Keep your coaching conversations and deal talk in one place.',
    extraExitPauseMs: 800,
  },
  '/executive/support': {
    label: 'Support',
    body: 'This is where you reach the team. Open a ticket, browse FAQs, or chat with us twenty-four seven — we respond fast.',
    extraExitPauseMs: 1000,
  },

  // ---- Radio Station Voice Tour -------------------------------------------
  // Same self-driving, locked, chaptered behavior as the Artist and Executive
  // tours; messaging framed around discovering new music, adding songs to
  // rotation, and engaging with submitting artists. Tour ends on Support.
  '/radio-station': {
    label: 'Dashboard',
    body: 'This is your station dashboard, your home base. Pending submissions, decided tracks, acceptance rate, top genres in your rotation, and your most recent submissions — all at a glance.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/radio-station/queue': {
    label: 'Submissions',
    body: 'Your submission queue. Independent artists pitch their music directly to your station here — listen, rate, and decide whether to add the track to your rotation or pass. Every decision strengthens the acceptance rate and genre insights on your dashboard.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/radio-station/stats': {
    label: 'Analytics',
    body: 'Deep analytics for your station — acceptance rate, top genres accepted versus submitted, unique artists reached, and decision trends over time. This is how you understand what your audience wants and what artists keep sending your way.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/radio-station/messages': {
    label: 'Messages',
    body: 'Direct messaging with artists whose submissions you decided on. Send feedback, request new edits, or build long-term relationships with the independent artists supplying your rotation.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/radio-station/profile': {
    label: 'Station Profile',
    body: 'Your public station profile — the page artists see when browsing stations to submit to. Set your call sign, market, genres, submission guidelines, and pricing. A complete profile gets you more high-quality submissions.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/radio-station/support': {
    label: 'Support',
    body: 'This is where you reach the team. Open a ticket, browse FAQs, or chat with us twenty-four seven — we respond fast.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/radio-station/invite-talent': {
    label: 'Invite Talent',
    body: 'Found an artist you want on your show? Invite them straight to your station. Drop in their email, add a personal note, and we will send them a branded invite to claim their spot — they sign up free and submit directly to you. You get ten free invites per day, unlimited while a boost is active. Track who you have invited and who has joined, all from this page.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/radio-station/hit-list': {
    label: 'Hit List',
    body: 'This is The Hit List — the chart of the highest‑rated independent songs across AvenueAR Pro, curated by A and R executives platform‑wide. You can listen to every track right here, see who is climbing, who is falling, and the plays, saves, and ratings behind each one. Hear something that fits your sound? Tap Add to Rotation or open a direct message with the artist. It is the fastest way to put something fresh on air without waiting for it to land in your queue.',
    autoScroll: true,
    extraExitPauseMs: 1000,
  },

  // ============== Playlist Curator ==============
  '/playlist-curator': {
    label: 'Dashboard',
    body: 'This is your curator dashboard, your home base. Pending submissions waiting on a decision, accepted tracks already added to your playlists, your acceptance rate, the genres climbing in your rotation, and the most recent songs hitting your queue — all in one view.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/playlist-curator/queue': {
    label: 'Submissions',
    body: 'Your submission queue. Independent artists pitch their music directly to your playlists. Listen, then accept or pass. Each decision affects your curator rating and feeds your analytics.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/playlist-curator/membership': {
    label: 'Membership',
    body: 'Your membership tier. Free curators charge a small fee per submission. Pro curators unlock featured placement on Discover, multi-playlist hosting, higher revenue share, and priority artist matching. Upgrade anytime.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/playlist-curator/stats': {
    label: 'Analytics',
    body: 'Curation analytics — acceptance rate, average response time, top genres accepted versus submitted, and revenue trends over time. Use these numbers to fine-tune your submission pricing and your public guidelines.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/playlist-curator/profile': {
    label: 'My Profile',
    body: 'Your public curator profile — what artists see before they submit. Add your playlist links, set genres, write submission guidelines, and lock in your per-submission price. The clearer this page, the better the submissions you receive.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
  '/playlist-curator/support': {
    label: 'Support',
    body: 'This is where you reach the team. Open a ticket, browse the FAQ, or chat with us twenty-four seven — we respond fast.',
    autoScroll: true,
    extraExitPauseMs: 800,
  },
};

/**
 * Live, mergable narration map. Starts as a clone of STATIC_PAGE_NARRATIONS
 * and gets selectively overridden by `applyVoiceTourOverrides()` whenever
 * the admin panel pushes a change.
 */
export const PAGE_NARRATIONS = { ...STATIC_PAGE_NARRATIONS };

// Tracks which paths are currently disabled via admin "enabled: false".
const _disabledPaths = new Set();
// Tracks which paths have an active admin override (used to hide the
// "Reset" button when there's nothing to reset back to).
const _overriddenPaths = new Set();

/**
 * Apply the admin overrides returned by GET /api/voice-tours/active onto
 * the live PAGE_NARRATIONS map. Idempotent — clears old overrides first
 * so deletes from the admin panel correctly fall back to defaults.
 */
export function applyVoiceTourOverrides(overrides) {
  // Reset to a clean copy of the defaults.
  Object.keys(PAGE_NARRATIONS).forEach((k) => delete PAGE_NARRATIONS[k]);
  Object.entries(STATIC_PAGE_NARRATIONS).forEach(([k, v]) => { PAGE_NARRATIONS[k] = v; });
  _disabledPaths.clear();
  _overriddenPaths.clear();

  if (!Array.isArray(overrides)) return;
  overrides.forEach((o) => {
    if (!o || !o.path) return;
    const base = STATIC_PAGE_NARRATIONS[o.path] || {};
    const merged = {
      ...base,
      ...(o.label ? { label: o.label } : {}),
      ...(o.body ? { body: o.body } : {}),
      ...(typeof o.auto_scroll === 'boolean' ? { autoScroll: o.auto_scroll } : {}),
      ...(typeof o.extra_exit_pause_ms === 'number' ? { extraExitPauseMs: o.extra_exit_pause_ms } : {}),
    };
    if (o.enabled === false) {
      _disabledPaths.add(o.path);
    } else {
      // Only register the merged narration if it actually has content.
      if (merged.label || merged.body || base.body) {
        PAGE_NARRATIONS[o.path] = merged;
      }
    }
    _overriddenPaths.add(o.path);
  });
}

export function isPathDisabled(path) {
  return _disabledPaths.has(path);
}
export function hasOverride(path) {
  return _overriddenPaths.has(path);
}

/**
 * Resolve a page narration by walking pathname prefixes (longest first) so
 * sub-routes like /artist/songs/abc123 still match /artist/songs. Returns
 * null when the page has been explicitly disabled by an admin override.
 */
export function findPageNarration(pathname) {
  if (!pathname) return null;
  if (_disabledPaths.has(pathname)) return null;
  if (PAGE_NARRATIONS[pathname]) return PAGE_NARRATIONS[pathname];
  const keys = Object.keys(PAGE_NARRATIONS).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (pathname.startsWith(k + '/') || pathname === k) return PAGE_NARRATIONS[k];
  }
  return null;
}
